import React, { useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import UserAvatar from '../../common/avatar/UserAvatar';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../common/pillTabs.css';
import AllProductList from './AllProductList';
import NewAddedProduct from './NewAddedProduct';
import CreatedEvent from './CreatedEvent';
import { toast } from 'react-toastify';
import { useConfirm } from '../../common/confirmDialog/useConfirm';
import { blockVenderApi, deleteVenderApi, resumeVenderApi } from '../../api';
import { useSelector } from 'react-redux';
import configuration from '../../config/Config';
import { formatMoney } from '../../utils/configUtils';
import StatusChip from '../../common/statusChip/StatusChip';

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', fontFamily: 'Heebo', wordBreak: 'break-word' }}>
      {children || '—'}
    </Box>
  </Box>
);

const TABS = ['All Products', 'New Added Products', 'Created Events'];

/* ─── Page ────────────────────────────────────────────────────────── */
const VenderDetails = () => {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { selectedRow } = useLocation().state || {};

  const [activeTab, setActiveTab] = useState(0);
  const [accStatus, setAccStatus] = useState(selectedRow?.account_status);

  const handleDelete = async () => {
    const ok = await confirm({ title: 'Delete Vendor', description: 'Are you sure you want to delete this vendor?', confirmText: 'Delete', destructive: true });
    if (!ok) return;
    try {
      const res = await deleteVenderApi({ token, id: selectedRow?._id });
      toast.success(res?.message);
      navigate(-1);
    } catch (e) { toast.error(e?.message || e?.error); }
  };

  const handleBlock = async () => {
    const ok = await confirm({ title: 'Block Vendor', description: 'Are you sure you want to block this vendor?', confirmText: 'Block' });
    if (!ok) return;
    try {
      const res = await blockVenderApi({ token, id: selectedRow._id, account_status: 3 });
      setAccStatus(3);
      toast.success(res.message);
    } catch (e) { toast.error(e?.message || e?.error); }
  };

  const handleResume = async () => {
    const ok = await confirm({ title: 'Resume Vendor', description: 'Are you sure you want to resume this vendor?', confirmText: 'Resume' });
    if (!ok) return;
    try {
      const res = await resumeVenderApi({ token, id: selectedRow._id, account_status: 2 });
      setAccStatus(2);
      toast.success(res?.message);
    } catch (e) { toast.error(e?.message || e?.error); }
  };

  const addr = selectedRow?.address;
  const addressLine = [addr?.address, addr?.city, addr?.state, addr?.country, addr?.zipcode].filter(Boolean).join(', ');
  const hasBanking = selectedRow?.bankName || selectedRow?.accountNo || selectedRow?.accountType;

  return (
    <Box sx={{ maxWidth: '1420px' }}>

      {/* Back */}
      <Box
        onClick={() => navigate(-1)}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, cursor: 'pointer', mt: 2, mb: 3, color: '#065F46', '&:hover': { opacity: 0.75 } }}
      >
        <ArrowBackIcon sx={{ fontSize: 20 }} />
        <Typography sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Heebo', color: '#065F46' }}>Back</Typography>
      </Box>

      {/* Profile card */}
      <Box sx={{ bgcolor: '#fff', borderRadius: '24px', boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)', p: { xs: 2.5, sm: 4 } }}>

        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo' }}>
            Vendor Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={handleDelete}
              size="small"
              sx={{ borderRadius: '20px', bgcolor: '#FEE2E2', color: '#991B1B', fontFamily: 'Heebo', fontWeight: 600, fontSize: 13, px: 2.5, textTransform: 'none', '&:hover': { bgcolor: '#fecaca' } }}
            >
              Delete
            </Button>
            <Button
              onClick={accStatus === 3 ? handleResume : handleBlock}
              size="small"
              sx={{ borderRadius: '20px', bgcolor: '#F3F4F6', color: '#362B24', fontFamily: 'Heebo', fontWeight: 600, fontSize: 13, px: 2.5, textTransform: 'none', '&:hover': { bgcolor: '#e5e7eb' } }}
            >
              {accStatus === 3 ? 'Resume' : 'Block'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Profile identity */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 2.5, bgcolor: '#F8F9FA', borderRadius: '18px', mb: 3, flexWrap: 'wrap' }}>
          <UserAvatar
            src={selectedRow?.picture ? `${configuration.cdnFront}/${selectedRow.picture}` : undefined}
            name={selectedRow?.fullname}
            size={72}
            sx={{ flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo', mb: 0.5 }}>
              {selectedRow?.fullname || '—'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {selectedRow?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <EmailOutlinedIcon sx={{ fontSize: 15, color: '#065F46' }} />
                  <Typography sx={{ fontSize: 13, color: '#065F46', fontFamily: 'Heebo', wordBreak: 'break-all' }}>{selectedRow.email}</Typography>
                </Box>
              )}
              {selectedRow?.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocalPhoneOutlinedIcon sx={{ fontSize: 15, color: '#065F46' }} />
                  <Typography sx={{ fontSize: 13, color: '#065F46', fontFamily: 'Heebo' }}>{selectedRow.phone}</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Total balance pill — right side */}
          <Box sx={{ ml: 'auto', textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.25 }}>
              Total Balance
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#065F46', fontFamily: 'Heebo' }}>
              {formatMoney(selectedRow?.totalAmount)}
            </Typography>
          </Box>
        </Box>

        {/* General fields */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(4,1fr)' }, gap: 3, mb: 3 }}>
          <Field label="Status"><StatusChip value={accStatus} /></Field>
          <Field label="Member Since">
            {selectedRow?.createdAt ? new Date(selectedRow.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
          </Field>
          <Field label="Bank Details">{selectedRow?.filled || '—'}</Field>
          <Field label="City">{addr?.city || '—'}</Field>
          <Field label="State">{addr?.state || '—'}</Field>
          <Field label="Country">{addr?.country || '—'}</Field>
          <Field label="Zip Code">{addr?.zipcode || '—'}</Field>
        </Box>

        {/* Banking section */}
        {hasBanking && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccountBalanceOutlinedIcon sx={{ fontSize: 16, color: '#065F46' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo' }}>
                Banking Details
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)' }, gap: 3, mb: 3 }}>
              <Field label="Bank Name">{selectedRow?.bankName || '—'}</Field>
              <Field label="Account No">{selectedRow?.accountNo || '—'}</Field>
              <Field label="Account Type">{selectedRow?.accountType || '—'}</Field>
            </Box>
          </>
        )}

        {/* Address row */}
        {addressLine && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 2, bgcolor: '#F8F9FA', borderRadius: '14px' }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 17, color: '#065F46', mt: 0.15, flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.3 }}>
                Address
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.6 }}>
                {addressLine}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ mt: 3 }}>
        <div className="pill-box" style={{ width: '460px', maxWidth: '100%' }}>
          <div
            className="pill-slider"
            style={{
              left: `calc(5px + ${activeTab} * (100% - 10px) / ${TABS.length})`,
              width: `calc((100% - 10px) / ${TABS.length})`,
            }}
          />
          {TABS.map((label, i) => (
            <button key={i} className={`pill-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
              {label}
            </button>
          ))}
        </div>
      </Box>

      {activeTab === 0 && <AllProductList />}
      {activeTab === 1 && <NewAddedProduct />}
      {activeTab === 2 && <CreatedEvent />}
    </Box>
  );
};

export default VenderDetails;
